import "./cohort.scss";
import React, { useState, useContext, useMemo } from "react";
import CohortGraph from "react-cohort-graph";
import {
    Card,
    Typography,
    Space,
    Button,
    Tooltip as AntTooltip,
    Statistic,
    Row,
    Col,
    Switch,
    Alert,
    Spin
} from "antd";
import {
    BarChartOutlined,
    InfoCircleOutlined,
    DownloadOutlined,
    EyeOutlined,
    EyeInvisibleOutlined,
    ReloadOutlined
} from "@ant-design/icons";
import { DarkModeContext } from "../../../shared/contexts/darkModeContext";
import { CSVLink } from "react-csv";
import dayjs from "dayjs";

const { Title, Text } = Typography;

const Cohort = ({
                    title,
                    data,
                    headerFormatter,
                    loading = false,
                    onRefresh,
                    showStats = true
                }) => {
    const { darkMode } = useContext(DarkModeContext);
    const [showDetails, setShowDetails] = useState(true);

    // 통계 계산을 위한 데이터 처리만 수행
    const cohortStats = useMemo(() => {
        if (!data || typeof data !== 'object' || Object.keys(data).length === 0) return null;

        try {
            const sortedKeys = Object.keys(data).sort((a, b) => new Date(b) - new Date(a));
            const totalCohorts = sortedKeys.length;

            // 월별 사용자 수 계산
            let totalUsers = 0;
            let day1Retentions = [];
            let allRetentions = [];

            sortedKeys.forEach(dateKey => {
                const retentionArray = data[dateKey];
                if (Array.isArray(retentionArray)) {
                    const users = retentionArray[0] || 0;
                    totalUsers += users;

                    // Day 1 리텐션 계산 (퍼센티지)
                    if (users > 0 && retentionArray[1] !== undefined) {
                        const day1Retention = (retentionArray[1] / users) * 100;
                        day1Retentions.push(day1Retention);
                    }

                    // 모든 리텐션 퍼센티지 계산
                    for (let i = 1; i < retentionArray.length; i++) {
                        if (users > 0) {
                            const retention = (retentionArray[i] / users) * 100;
                            allRetentions.push(retention);
                        }
                    }
                }
            });

            const avgRetention = day1Retentions.length > 0
                ? day1Retentions.reduce((sum, retention) => sum + retention, 0) / day1Retentions.length
                : 0;

            const bestRetention = allRetentions.length > 0 ? Math.max(...allRetentions) : 0;

            return {
                totalCohorts,
                totalUsers,
                avgRetention: avgRetention.toFixed(2),
                bestRetention: bestRetention.toFixed(2)
            };
        } catch (error) {
            console.error('Error calculating cohort stats:', error);
            return null;
        }
    }, [data]);

    // CSV 다운로드용 데이터 변환
    const csvData = useMemo(() => {
        if (!data || typeof data !== 'object' || Object.keys(data).length === 0) return [];

        try {
            const sortedKeys = Object.keys(data).sort((a, b) => new Date(b) - new Date(a));

            return sortedKeys.map(dateKey => {
                const retentionArray = data[dateKey];
                if (!Array.isArray(retentionArray)) return null;

                const users = retentionArray[0] || 0;
                const row = {
                    '날짜': dateKey,
                    '가입자 수': users.toLocaleString(),
                };

                // 리텐션 데이터 추가
                retentionArray.slice(1).forEach((count, dayIndex) => {
                    let dayLabel;
                    if (dayIndex === 0) dayLabel = 'Day 1';
                    else if (dayIndex === 1) dayLabel = 'Day 2';
                    else if (dayIndex === 2) dayLabel = 'Day 3';
                    else if (dayIndex === 3) dayLabel = 'Day 4';
                    else if (dayIndex === 4) dayLabel = 'Day 5';
                    else if (dayIndex === 5) dayLabel = 'Day 6';
                    else if (dayIndex === 6) dayLabel = 'Day 7';
                    else if (dayIndex === 7) dayLabel = 'Day 14';
                    else if (dayIndex === 8) dayLabel = 'Day 30';
                    else dayLabel = `Day ${dayIndex + 1}`;

                    const percentage = users > 0 ? ((count / users) * 100).toFixed(2) : '0.00';
                    row[dayLabel] = `${percentage}%`;
                });

                return row;
            }).filter(item => item !== null);
        } catch (error) {
            console.error('Error generating CSV data:', error);
            return [];
        }
    }, [data]);

    if (loading) {
        return (
            <Card
                className={`cohort-card ${darkMode ? 'dark-theme' : 'light-theme'}`}
                title={
                    <Space align="center">
                        <div className="chart-icon">
                            <BarChartOutlined />
                        </div>
                        <Title level={5} style={{ margin: 0, color: 'var(--text-primary)' }}>
                            {title}
                        </Title>
                    </Space>
                }
                style={{
                    background: 'var(--card-background)',
                    border: '1px solid var(--border-color)',
                    borderRadius: '12px',
                    boxShadow: 'var(--card-shadow)'
                }}
            >
                <div className="cohort-loading">
                    <Spin size="large" />
                    <Text type="secondary" style={{ marginTop: 16, display: 'block' }}>
                        코호트 데이터를 불러오는 중...
                    </Text>
                </div>
            </Card>
        );
    }

    if (!data || typeof data !== 'object' || Object.keys(data).length === 0) {
        return (
            <Card
                className={`cohort-card ${darkMode ? 'dark-theme' : 'light-theme'}`}
                title={
                    <Space align="center">
                        <div className="chart-icon">
                            <BarChartOutlined />
                        </div>
                        <Title level={5} style={{ margin: 0, color: 'var(--text-primary)' }}>
                            {title}
                        </Title>
                    </Space>
                }
                style={{
                    background: 'var(--card-background)',
                    border: '1px solid var(--border-color)',
                    borderRadius: '12px',
                    boxShadow: 'var(--card-shadow)'
                }}
            >
                <Alert
                    message="데이터 없음"
                    description="표시할 코호트 데이터가 없습니다."
                    type="info"
                    showIcon
                    icon={<InfoCircleOutlined />}
                />
            </Card>
        );
    }

    return (
        <Card
            className={`cohort-card ${darkMode ? 'dark-theme' : 'light-theme'}`}
            title={
                <Space align="center">
                    <div className="chart-icon">
                        <BarChartOutlined />
                    </div>
                    <div>
                        <Title level={5} style={{ margin: 0, color: 'var(--text-primary)' }}>
                            {title}
                        </Title>
                        <Text type="secondary" style={{ fontSize: '12px' }}>
                            사용자 리텐션 분석
                        </Text>
                    </div>
                </Space>
            }
            extra={
                <Space>
                    {showStats && (
                        <AntTooltip title={showDetails ? "상세 정보 숨기기" : "상세 정보 표시"}>
                            <Switch
                                checked={showDetails}
                                onChange={setShowDetails}
                                checkedChildren={<EyeOutlined />}
                                unCheckedChildren={<EyeInvisibleOutlined />}
                                size="small"
                            />
                        </AntTooltip>
                    )}
                    {onRefresh && (
                        <AntTooltip title="데이터 새로고침">
                            <Button
                                type="text"
                                icon={<ReloadOutlined />}
                                onClick={onRefresh}
                                size="small"
                            />
                        </AntTooltip>
                    )}
                    {Object.keys(data).length > 0 && (
                        <CSVLink
                            data={csvData}
                            filename={`cohort_analysis_${dayjs().format('YYYYMMDDHHmmss')}.csv`}
                            style={{ textDecoration: 'none' }}
                        >
                            <AntTooltip title="CSV 다운로드">
                                <Button
                                    type="primary"
                                    icon={<DownloadOutlined />}
                                    size="small"
                                    className="download-btn"
                                >
                                    CSV
                                </Button>
                            </AntTooltip>
                        </CSVLink>
                    )}
                </Space>
            }
            style={{
                background: 'var(--card-background)',
                border: '1px solid var(--border-color)',
                borderRadius: '12px',
                boxShadow: 'var(--card-shadow)'
            }}
        >
            <div className="cohort-container">
                {/* 통계 정보 */}
                {showStats && showDetails && cohortStats && (
                    <div className="cohort-stats" style={{ marginBottom: '24px' }}>
                        <Row gutter={[16, 16]}>
                            <Col xs={12} sm={6}>
                                <Statistic
                                    title="총 코호트 수"
                                    value={cohortStats.totalCohorts}
                                    valueStyle={{
                                        color: 'var(--text-primary)',
                                        fontSize: '18px'
                                    }}
                                />
                            </Col>
                            <Col xs={12} sm={6}>
                                <Statistic
                                    title="월별 사용자 수"
                                    value={cohortStats.totalUsers}
                                    valueStyle={{
                                        color: '#1890ff',
                                        fontSize: '18px'
                                    }}
                                />
                            </Col>
                            <Col xs={12} sm={6}>
                                <Statistic
                                    title="평균 Day1 리텐션"
                                    value={cohortStats.avgRetention}
                                    suffix="%"
                                    valueStyle={{
                                        color: '#52c41a',
                                        fontSize: '18px'
                                    }}
                                />
                            </Col>
                            <Col xs={12} sm={6}>
                                <Statistic
                                    title="최고 리텐션"
                                    value={cohortStats.bestRetention}
                                    suffix="%"
                                    valueStyle={{
                                        color: '#fa8c16',
                                        fontSize: '18px'
                                    }}
                                />
                            </Col>
                        </Row>
                    </div>
                )}

                {/* 코호트 그래프 - 기존 방식 그대로 사용 */}
                <div className="cohort-graph-wrapper">
                    <div className="graph-container">
                        <CohortGraph
                            key={JSON.stringify(data)}
                            data={{
                                days: data
                            }}
                            title={title}
                            headerFormatter={headerFormatter}
                        />
                    </div>
                </div>

                {/* 설명 텍스트 */}
                {showDetails && (
                    <div className="cohort-description" style={{ marginTop: '16px' }}>
                        <Space direction="vertical" size="small">
                            <Text type="secondary" style={{ fontSize: '12px' }}>
                                💡 <strong>코호트 분석이란?</strong> 특정 기간에 가입한 사용자 그룹의 시간별 리텐션을 분석하는 방법입니다.
                            </Text>
                            <Text type="secondary" style={{ fontSize: '12px' }}>
                                📊 각 셀의 색상은 리텐션 비율을 나타내며, 진한 색일수록 높은 리텐션을 의미합니다.
                            </Text>
                            <Text type="secondary" style={{ fontSize: '12px' }}>
                                📈 첫 번째 열은 해당 날짜의 신규 가입자 수를 나타냅니다.
                            </Text>
                        </Space>
                    </div>
                )}
            </div>
        </Card>
    );
};

export default Cohort;